import {
    usePipecatClient,
    usePipecatClientMicControl,
} from "@pipecat-ai/client-react";
import { usePipecatConnectionState } from "@pipecat-ai/voice-ui-kit";
import { useTranslation } from "react-i18next";
import { getBackendEndpoint } from "@/config";
import { MicIcon } from "./MicIcon";

function MicOffIcon({ className = "size-4" }: { className?: string }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden="true"
        >
            <line x1="2" y1="2" x2="22" y2="22" />
            <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
            <path d="M15 9.34V5a3 3 0 0 0-5.94-.6" />
            <path d="M19 10v2a7 7 0 0 1-.31 2.05" />
            <path d="M5 10v2a7 7 0 0 0 12 5" />
            <line x1="12" y1="19" x2="12" y2="23" />
        </svg>
    );
}

function PhoneStartIcon({ className = "size-5" }: { className?: string }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden="true"
        >
            <path d="M19.95 17.36 17.7 15.1a1.5 1.5 0 0 0-1.7-.3l-2.18.97a1 1 0 0 1-1.07-.2l-3.32-3.32a1 1 0 0 1-.2-1.07l.97-2.18a1.5 1.5 0 0 0-.3-1.7L7.64 4.05a1.5 1.5 0 0 0-2.12 0L4.3 5.27a3 3 0 0 0-.79 2.83A18 18 0 0 0 15.9 20.5a3 3 0 0 0 2.83-.79l1.22-1.22a1.5 1.5 0 0 0 0-2.12z" />
        </svg>
    );
}

function PhoneEndIcon({ className = "size-5" }: { className?: string }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden="true"
        >
            <path d="M12 9c-2.13 0-4.18.36-6.07 1.03a1.5 1.5 0 0 0-1 1.42v2.18a1.5 1.5 0 0 0 .92 1.39l1.83.77a1.5 1.5 0 0 0 1.7-.32l1.4-1.4a1 1 0 0 1 .56-.28 16.7 16.7 0 0 1 5.32 0 1 1 0 0 1 .56.28l1.4 1.4a1.5 1.5 0 0 0 1.7.32l1.83-.77a1.5 1.5 0 0 0 .92-1.39v-2.18a1.5 1.5 0 0 0-1-1.42A19 19 0 0 0 12 9z" />
        </svg>
    );
}

type CircularButtonProps = {
    onClick: () => void;
    disabled?: boolean;
    label: string;
    helper?: string;
    children: React.ReactNode;
    tone: "neutral" | "danger" | "primary" | "muted";
    size?: "md" | "lg";
};

function CircularButton({
    onClick,
    disabled,
    label,
    helper,
    children,
    tone,
    size = "md",
}: CircularButtonProps) {
    const sizeClass = size === "lg" ? "size-16" : "size-14";

    const toneClass =
        tone === "danger"
            ? "bg-red-500 text-white shadow-[0_12px_28px_-8px_rgba(239,68,68,0.55)] hover:bg-red-600 active:scale-95"
            : tone === "primary"
              ? "bg-emerald-500 text-white shadow-[0_12px_28px_-8px_rgba(16,185,129,0.55)] hover:bg-emerald-600 active:scale-95"
              : tone === "muted"
                ? "bg-zinc-800 text-white shadow-[0_10px_24px_-10px_rgba(24,24,27,0.6)] hover:bg-zinc-700 active:scale-95"
                : "bg-white text-zinc-700 ring-1 ring-zinc-200 shadow-sm hover:bg-zinc-50 active:scale-95";

    return (
        <div className="flex flex-col items-center gap-2">
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                className={`flex ${sizeClass} items-center justify-center rounded-full transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${toneClass}`}
            >
                {children}
            </button>
            <span className="text-[11px] font-medium text-zinc-500">
                {helper ?? label}
            </span>
        </div>
    );
}

export function Controls() {
    const { t } = useTranslation();
    const client = usePipecatClient()!;
    const { isConnected, isConnecting } = usePipecatConnectionState();
    const { isMicEnabled, enableMic } = usePipecatClientMicControl();

    async function handleConnect() {
        try {
            await client.startBotAndConnect({
                endpoint: getBackendEndpoint(),
                headers: new Headers({
                    "ngrok-skip-browser-warning": "true",
                }),
            });
        } catch (err) {
            console.error("Failed to start session", err);
        }
    }

    async function handleDisconnect() {
        try {
            await client.disconnect();
        } catch (err) {
            console.error("Failed to disconnect", err);
        }
    }

    function handleMicToggle() {
        enableMic(!isMicEnabled);
    }

    return (
        <div className="shrink-0 px-6 pb-8 pt-4 sm:pb-10">
            <div className="flex items-end justify-center gap-6 sm:gap-8">
                {isConnected && (
                    <CircularButton
                        onClick={handleMicToggle}
                        label={
                            isMicEnabled
                                ? t("controls.mic.on")
                                : t("controls.mic.off")
                        }
                        helper={
                            isMicEnabled
                                ? t("controls.mic.on")
                                : t("controls.mic.off")
                        }
                        tone={isMicEnabled ? "neutral" : "muted"}
                    >
                        {isMicEnabled ? (
                            <MicIcon className="size-5" />
                        ) : (
                            <MicOffIcon className="size-5" />
                        )}
                    </CircularButton>
                )}

                <CircularButton
                    onClick={isConnected ? handleDisconnect : handleConnect}
                    disabled={isConnecting}
                    size="lg"
                    label={
                        isConnected
                            ? t("controls.button.end")
                            : isConnecting
                              ? t("controls.button.connecting")
                              : t("controls.button.start")
                    }
                    helper={
                        isConnected
                            ? t("controls.button.end")
                            : isConnecting
                              ? t("controls.button.connecting")
                              : t("controls.button.start")
                    }
                    tone={isConnected ? "danger" : "primary"}
                >
                    {isConnecting ? (
                        <svg
                            className="size-5 animate-spin"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                        >
                            <path
                                strokeLinecap="round"
                                d="M21 12a9 9 0 1 1-6.219-8.56"
                            />
                        </svg>
                    ) : isConnected ? (
                        <PhoneEndIcon className="size-6 rotate-[135deg]" />
                    ) : (
                        <PhoneStartIcon className="size-6" />
                    )}
                </CircularButton>
            </div>
        </div>
    );
}
